import { c as _c } from "react/compiler-runtime";
import { Table as ComponentsTable } from '@systemeio/components';
import { twMerge } from 'tailwind-merge';
import { resolveRowId } from '../utils';
import { getRowClassName, getRowClickHandler, getRowTestAttributes } from '../utils/table-row-helpers';
import { useSortableRow } from '../utils/use-sortable-row';
import { ColumnLoaderRows } from './column-loader-rows';
import type { ColumnRowProps, DesktopViewProps, RowsProps } from './view-props';
function ColumnRow(t0) {
  const $ = _c(21);
  const {
    row,
    visibleColumns,
    totalWidth,
    testAttributePostfix,
    isSortable,
    rowClassName,
    getRowId,
    onRowClick
  } = t0;
  const rowId = resolveRowId({
    row,
    getRowId
  });
  const {
    attributes,
    isDragging,
    listeners,
    setNodeRef,
    style
  } = useSortableRow({
    rowId,
    isSortable
  });
  const resolvedRowClassName = getRowClassName(row, rowClassName);
  let t1;
  if ($[0] !== onRowClick || $[1] !== row) {
    t1 = getRowClickHandler(row, onRowClick);
    $[0] = onRowClick;
    $[1] = row;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const rowClickHandler = t1;
  const testAttributes = getRowTestAttributes(rowId, testAttributePostfix);
  const t2 = ComponentsTable;
  const t3 = twMerge(isDragging && "border-border-accent border-dashed", isSortable ? "cursor-grab" : onRowClick ? "cursor-pointer" : "cursor-auto", resolvedRowClassName);
  let t4;
  if ($[3] !== row || $[4] !== totalWidth || $[5] !== visibleColumns) {
    let t5;
    if ($[7] !== row || $[8] !== totalWidth) {
      t5 = (column, index) => {
        const width = `${(column.widthPercent || 25) / totalWidth * 100}%`;
        const renderMethod = column.onRender;
        const content = renderMethod(row);
        return <ComponentsTable.Cell key={`${column.key}-${index}`} className={column.className} style={{
          width
        }}>{content}</ComponentsTable.Cell>;
      };
      $[7] = row;
      $[8] = totalWidth;
      $[9] = t5;
    } else {
      t5 = $[9];
    }
    t4 = visibleColumns.map(t5);
    $[3] = row;
    $[4] = totalWidth;
    $[5] = visibleColumns;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[10] !== attributes || $[11] !== listeners || $[12] !== rowClickHandler || $[13] !== rowId || $[14] !== setNodeRef || $[15] !== style || $[16] !== t2.Row || $[17] !== t3 || $[18] !== t4 || $[19] !== testAttributes) {
    t5 = <t2.Row ref={setNodeRef} id={rowId} style={style} onClick={rowClickHandler} className={t3} {...attributes} {...listeners} {...testAttributes}>{t4}</t2.Row>;
    $[10] = attributes;
    $[11] = listeners;
    $[12] = rowClickHandler;
    $[13] = rowId;
    $[14] = setNodeRef;
    $[15] = style;
    $[16] = t2.Row;
    $[17] = t3;
    $[18] = t4;
    $[19] = testAttributes;
    $[20] = t5;
  } else {
    t5 = $[20];
  }
  return t5;
}
function ColumnRows(t0) {
  const $ = _c(10);
  const {
    data,
    limiter,
    visibleColumns,
    totalWidth,
    testAttributePostfix,
    isSortable,
    rowClassName,
    getRowId,
    onRowClick
  } = t0;
  let t1;
  if ($[0] !== data || $[1] !== getRowId || $[2] !== isSortable || $[3] !== limiter || $[4] !== onRowClick || $[5] !== rowClassName || $[6] !== testAttributePostfix || $[7] !== totalWidth || $[8] !== visibleColumns) {
    t1 = data === undefined ? <ColumnLoaderRows limiter={limiter} visibleColumns={visibleColumns} totalWidth={totalWidth} /> : <ComponentsTable.Body>{data.items.map(row => <ColumnRow key={resolveRowId({
        row,
        getRowId
      })} row={row} visibleColumns={visibleColumns} totalWidth={totalWidth} testAttributePostfix={testAttributePostfix} isSortable={isSortable} rowClassName={rowClassName} getRowId={getRowId} onRowClick={onRowClick} />)}</ComponentsTable.Body>;
    $[0] = data;
    $[1] = getRowId;
    $[2] = isSortable;
    $[3] = limiter;
    $[4] = onRowClick;
    $[5] = rowClassName;
    $[6] = testAttributePostfix;
    $[7] = totalWidth;
    $[8] = visibleColumns;
    $[9] = t1;
  } else {
    t1 = $[9];
  }
  return t1;
}
export function DesktopView(t0) {
  const $ = _c(27);
  let allowSelectAll;
  let columns;
  let noHeader;
  let onSortChange;
  let rowsProps;
  let selectedAll;
  let selectionProps;
  let sortDescriptor;
  if ($[0] !== t0) {
    ({
      columns,
      noHeader,
      selectedAll,
      allowSelectAll,
      sortDescriptor,
      onSortChange,
      selectionProps,
      ...rowsProps
    } = t0);
    $[0] = t0;
    $[1] = allowSelectAll;
    $[2] = columns;
    $[3] = noHeader;
    $[4] = onSortChange;
    $[5] = rowsProps;
    $[6] = selectedAll;
    $[7] = selectionProps;
    $[8] = sortDescriptor;
  } else {
    allowSelectAll = $[1];
    columns = $[2];
    noHeader = $[3];
    onSortChange = $[4];
    rowsProps = $[5];
    selectedAll = $[6];
    selectionProps = $[7];
    sortDescriptor = $[8];
  }
  const {
    totalWidth
  } = rowsProps;
  const t1 = noHeader ? "hidden" : undefined;
  let t2;
  if ($[9] !== columns || $[10] !== totalWidth) {
    let t3;
    if ($[12] !== totalWidth) {
      t3 = (column, index) => {
        const width = `${(column.widthPercent || 25) / totalWidth * 100}%`;
        return <ComponentsTable.Head id={column.key} key={`${column.key}-${index}`} className={column.className} allowsSorting={!!column.sort} style={{
          width
        }}>{column.header}</ComponentsTable.Head>;
      };
      $[12] = totalWidth;
      $[13] = t3;
    } else {
      t3 = $[13];
    }
    t2 = columns.map(t3);
    $[9] = columns;
    $[10] = totalWidth;
    $[11] = t2;
  } else {
    t2 = $[11];
  }
  let t3;
  if ($[14] !== allowSelectAll || $[15] !== selectedAll || $[16] !== t1 || $[17] !== t2) {
    t3 = <ComponentsTable.Header selectedAll={selectedAll} allowSelectAll={allowSelectAll} className={t1}>{t2}</ComponentsTable.Header>;
    $[14] = allowSelectAll;
    $[15] = selectedAll;
    $[16] = t1;
    $[17] = t2;
    $[18] = t3;
  } else {
    t3 = $[18];
  }
  let t4;
  if ($[19] !== rowsProps) {
    t4 = <ColumnRows {...rowsProps} />;
    $[19] = rowsProps;
    $[20] = t4;
  } else {
    t4 = $[20];
  }
  let t5;
  if ($[21] !== onSortChange || $[22] !== selectionProps || $[23] !== sortDescriptor || $[24] !== t3 || $[25] !== t4) {
    t5 = <ComponentsTable.Root className="table-fixed" sortDescriptor={sortDescriptor} onSortChange={onSortChange} {...selectionProps}>{t3}{t4}</ComponentsTable.Root>;
    $[21] = onSortChange;
    $[22] = selectionProps;
    $[23] = sortDescriptor;
    $[24] = t3;
    $[25] = t4;
    $[26] = t5;
  } else {
    t5 = $[26];
  }
  return t5;
}